import {
  AccountingSystemForCalculatingFinancialStatistics,
  CostOfTutoringBreakdown,
  OrganizationSubjectAdminResponse,
  RevenueBreakdown,
} from "client/openapi";

import FinancialBreakdown from "./Breakdown";
import TimeSeries from "../Insights/TimeSeries";
import PeriodTable from "../Insights/PeriodTable";
import { Range } from "../Insights/data";

export const fmtUSD = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export const toDollars = (c?: number) => (c ?? 0) / 100;

function LoadingCard({ label }: { label: string }) {
  return (
    <div className="space-y-4 bg-blue-50/50 p-6 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex flex-col items-center justify-center text-gray-500 gap-3 h-48">
        <div
          className="spinner-border text-gray-400"
          role="status"
          aria-label={label}
        />
        <span className="text-sm">{label}</span>
      </div>
    </div>
  );
}

/**
 * Insights, top to bottom: the time series (revenue, cost, net and hours,
 * with the compared dates when comparing), the breakdown, and the table.
 * Each always shows, even when the dates hold no money.
 */
export default function FinancialVisualizations({
  revenue,
  cost,
  orgId,
  zone,
  range,
  compared,
  basis,
  subjects,
  isFinancialsLoading,
}: {
  revenue?: RevenueBreakdown;
  cost?: CostOfTutoringBreakdown;
  orgId: number;
  zone: string;
  range: Range;
  compared: Range | null;
  basis: AccountingSystemForCalculatingFinancialStatistics;
  subjects: OrganizationSubjectAdminResponse[];
  isFinancialsLoading: boolean;
}) {
  const query = { orgId, range, compared, basis, subjects };
  return (
    <div className="space-y-12">
      <div>
        <h2 className="font-bold text-xl mb-4">Financial Time Series</h2>
        <TimeSeries {...query} zone={zone} />
      </div>
      <div>
        <h2 className="font-bold text-xl mb-4">Financial Breakdown</h2>
        {isFinancialsLoading ? (
          <LoadingCard label="Loading breakdown data" />
        ) : revenue && cost ? (
          <FinancialBreakdown revenue={revenue} cost={cost} />
        ) : (
          <div className="bg-blue-50/50 p-6 rounded-lg border border-gray-200 shadow-sm text-sm text-gray-500">
            Couldn't load the breakdown.
          </div>
        )}
      </div>
      <div>
        <h2 className="font-bold text-xl mb-4">Totals by period</h2>
        <PeriodTable {...query} />
      </div>
    </div>
  );
}
